import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

import { ExpressionField } from '../../../common';
import { FieldFormulaBase } from '../field-formula.base';
import { FieldFormulaComponent } from '../field-formula.component';
import { FormulaResult, FormulaToken } from '../formula';
import { FieldFormulaAutocompleteComponent } from '../formula-autocomplete/formula-autocomplete.component';
import { FieldFormulaEditorComponent } from '../formula-editor/formula-editor.component';

export type FormulaView = 'builder' | 'autocomplete' | 'editor';

@Component({
  selector: 'cipo-field-formula-views',
  imports: [
    FieldFormulaAutocompleteComponent,
    FieldFormulaComponent,
    FieldFormulaEditorComponent,
    FlexLayoutModule,
    MatButtonToggleModule,
    TranslateModule,
  ],
  templateUrl: './formula-views.component.html',
  host: { class: 'cipo--display--block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldFormulaViewsComponent {
  readonly moduleId = input.required<number>();
  readonly fieldId = input<number>(null);
  readonly expression = input<string>('');
  readonly isDateResult = input(false);
  readonly disabled = input(false);
  readonly fields = input<ExpressionField[]>();

  readonly view = signal<FormulaView>('builder');

  private readonly builder = viewChild(FieldFormulaComponent);
  private readonly typed = viewChild(FieldFormulaBase);
  private readonly editor = computed(() => this.builder() ?? this.typed());
  private carried: FormulaToken[] = null;

  readonly loading = computed(() => !!this.editor()?.loading());
  readonly changed = computed(() => !!this.editor()?.changed());
  readonly tokens = computed(() => this.editor()?.tokens() ?? []);

  constructor() {
    // The editor switched to starts from the unsaved formula, once it has loaded its fields.
    effect(() => {
      const editor = this.editor();
      if (!editor || !this.carried || editor.loading()) {
        return;
      }
      const tokens = this.carried;
      this.carried = null;
      untracked(() => editor.tokens.set(tokens));
    });
  }

  switchView(view: FormulaView): void {
    const editor = this.editor();
    this.carried = editor && !editor.loading() ? editor.tokens() : null;
    this.view.set(view);
  }

  result(): FormulaResult {
    return this.editor()?.result() ?? null;
  }
}
